import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

const PROFILE_TYPES = [
  { value: 'artist', label: 'Artist' },
  { value: 'curator', label: 'Curator' },
  { value: 'label', label: 'Label' },
  { value: 'other', label: 'Other' },
];

const SOCIAL_FIELDS = [
  ['website', 'Website'],
  ['spotify', 'Spotify'],
  ['instagram', 'Instagram'],
  ['facebook', 'Facebook'],
  ['tiktok', 'TikTok'],
  ['youtube', 'YouTube'],
  ['x', 'X'],
  ['soundcloud', 'SoundCloud'],
  ['bandcamp', 'Bandcamp'],
  ['apple_music', 'Apple Music'],
  ['beatport', 'Beatport'],
  ['deezer', 'Deezer'],
  ['tidal', 'TIDAL'],
];

export default function Profile() {
  const { user, setUser } = useAuth();

  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');

  const [form, setForm] = useState({
    name: '',
    profile_type: 'artist',
    bio: '',
    social_links: {},
    email_public: false,
  });

  const [picture, setPicture] = useState(null);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerPanY, setBannerPanY] = useState(0.5);
  const bannerDragRef = useRef(null);

  const publicUrl = useMemo(() => {
    if (!user?.user_id) return '';
    return `${window.location.origin}/profile/${user.user_id}`;
  }, [user?.user_id]);

  const loadProfile = async () => {
    setLoading(true);

    try {
      const { data } = await api.get('/profiles/me');

      setForm({
        name: data.name || '',
        profile_type: data.profile_type || 'artist',
        bio: data.bio || '',
        social_links: data.social_links || {},
        email_public: Boolean(data.email_public),
      });

      setPicture(data.picture || null);
      setBannerUrl(data.banner_url || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load your profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const updateField = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateSocial = (key, value) => {
    setForm(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [key]: value,
      },
    }));
  };

  const saveProfile = async () => {
    setSaving(true);

    try {
      const { data } = await api.put('/profiles/me', form);

      setForm({
        name: data.name || '',
        profile_type: data.profile_type || 'artist',
        bio: data.bio || '',
        social_links: data.social_links || {},
        email_public: Boolean(data.email_public),
      });

      setPicture(data.picture || null);
      setBannerUrl(data.banner_url || null);

      setUser(prev => prev ? {
        ...prev,
        name: data.name,
        picture: data.picture,
        profile_type: data.profile_type,
        bio: data.bio,
        banner_url: data.banner_url,
        social_links: data.social_links,
        email_public: Boolean(data.email_public),
      } : prev);

      toast.success('Profile saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (kind, file) => {
    if (!file) return;

    if (kind === 'banner') {
      const previewUrl = URL.createObjectURL(file);
      setBannerPreview(previewUrl);
      setBannerPanY(0.5);
      return;
    }

    setUploading(kind);

    try {
      const fd = new FormData();
      fd.append('file', file);

      const { data } = await api.post(
        `/profiles/me/${kind}`,
        fd,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      if (kind === 'avatar') {
        setPicture(data.url);
        setUser(prev => prev ? { ...prev, picture: data.url } : prev);
      }

      toast.success('Profile picture updated');
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
        `Could not upload ${kind}`
      );
    } finally {
      setUploading('');
    }
  };

  const saveBanner = async () => {
    if (!bannerPreview) return;

    setUploading('banner');

    try {
      const image = new Image();

      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = bannerPreview;
      });

      const outputWidth = 1280;
      const outputHeight = 256;

      const scale = Math.max(
        outputWidth / image.naturalWidth,
        outputHeight / image.naturalHeight,
      );

      const scaledWidth = image.naturalWidth * scale;
      const scaledHeight = image.naturalHeight * scale;
      const overflowY = Math.max(0, scaledHeight - outputHeight);
      const drawY = -overflowY * bannerPanY;

      const canvas = document.createElement('canvas');
      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        (outputWidth - scaledWidth) / 2,
        drawY,
        scaledWidth,
        scaledHeight,
      );

      const blob = await new Promise(resolve =>
        canvas.toBlob(resolve, 'image/jpeg', 0.9)
      );

      if (!blob) {
        throw new Error('Could not prepare banner image');
      }

      const fd = new FormData();
      fd.append('file', blob, 'banner.jpg');

      const { data } = await api.post(
        '/profiles/me/banner',
        fd,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      setBannerUrl(data.url);
      setUser(prev => prev ? { ...prev, banner_url: data.url } : prev);

      URL.revokeObjectURL(bannerPreview);
      setBannerPreview(null);
      setBannerPanY(0.5);

      toast.success('Banner updated');
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not upload banner'
      );
    } finally {
      setUploading('');
    }
  };

  const cancelBanner = () => {
    if (bannerPreview) {
      URL.revokeObjectURL(bannerPreview);
    }

    setBannerPreview(null);
    setBannerPanY(0.5);
  };

  const handleBannerPointerDown = e => {
    if (!bannerPreview) return;

    e.currentTarget.setPointerCapture(e.pointerId);

    bannerDragRef.current = {
      startY: e.clientY,
      startPanY: bannerPanY,
    };
  };

  const handleBannerPointerMove = e => {
    const drag = bannerDragRef.current;
    if (!drag) return;

    const delta = e.clientY - drag.startY;
    const nextPanY = Math.max(
      0,
      Math.min(1, drag.startPanY - delta / 220)
    );

    setBannerPanY(nextPanY);
  };

  const handleBannerPointerUp = () => {
    bannerDragRef.current = null;
  };

  const copyPublicLink = async () => {
    if (!publicUrl) return;

    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Public profile link copied');
    } catch {
      toast.error('Could not copy the profile link');
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-[1100px]">
        <div className="text-sm text-zinc-500">Loading profile…</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1100px]">
      <header className="mb-8">
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
          Account
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">
          Your profile.
        </h1>
        <p className="text-zinc-400 mt-1 text-sm max-w-2xl">
          This is the public identity other artists, curators and labels can see on TuneAmplifier.
        </p>
      </header>

      <div className="space-y-6">
        <section className="border border-zinc-800 rounded-2xl bg-zinc-900/40 overflow-hidden">
          <div
            className={`relative h-48 bg-zinc-950 overflow-hidden ${
              bannerPreview ? 'cursor-grab select-none' : ''
            }`}
            style={bannerPreview ? { touchAction: 'none' } : undefined}
          >
            {bannerPreview ? (
              <>
                <div
                  className="absolute inset-0 cursor-grab select-none"
                  style={{ touchAction: 'none' }}
                  onPointerDown={handleBannerPointerDown}
                  onPointerMove={handleBannerPointerMove}
                  onPointerUp={handleBannerPointerUp}
                  onPointerCancel={handleBannerPointerUp}
                >
                  <img
                    src={bannerPreview}
                    alt=""
                    draggable={false}
                    className="w-full h-full object-cover pointer-events-none"
                    style={{
                      objectPosition: `50% ${bannerPanY * 100}%`,
                    }}
                  />

                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="rounded-full bg-black/60 px-4 py-2 text-xs text-white font-mono">
                      Drag up / down to position
                    </div>
                  </div>
                </div>

                <div className="absolute inset-x-0 bottom-0 z-10 p-4 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={cancelBanner}
                    disabled={uploading === 'banner'}
                    className="bg-black/60 border-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={saveBanner}
                    disabled={uploading === 'banner'}
                    className="bg-lime-400 text-black hover:bg-lime-300"
                  >
                    {uploading === 'banner' ? 'Saving…' : 'Save banner'}
                  </Button>
                </div>
              </>
            ) : bannerUrl ? (
              <img
                src={bannerUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-zinc-600 font-mono uppercase tracking-wider">
                No banner yet
              </div>
            )}

            {!bannerPreview && (
              <div className="absolute inset-x-0 bottom-0 p-4 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  disabled={uploading === 'banner'}
                  onClick={() => bannerInputRef.current?.click()}
                  className="bg-black/60 border-zinc-700"
                >
                  Change banner
                </Button>
              </div>
            )}

            <input
              ref={bannerInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={e => {
                uploadImage('banner', e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </div>

          <div className="p-6">
            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                className="relative -mt-16 shrink-0 rounded-full"
                title="Change profile picture"
              >
                {picture ? (
                  <img
                    src={picture}
                    alt=""
                    className="w-24 h-24 rounded-full object-cover border-4 border-zinc-950 bg-zinc-900"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full border-4 border-zinc-950 bg-zinc-800 flex items-center justify-center text-zinc-500">
                    Photo
                  </div>
                )}
              </button>

              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => {
                  uploadImage('avatar', e.target.files?.[0]);
                  e.target.value = '';
                }}
              />

              <div className="pt-2">
                <div className="font-display text-xl">{form.name || 'Your name'}</div>
                <div className="text-xs text-zinc-600 mt-1">
                  Click the profile picture to change it.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border border-zinc-800 rounded-2xl bg-zinc-900/40 p-6">
          <div className="font-display text-xl mb-5">Profile information</div>

          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                Display name
              </label>
              <Input
                value={form.name}
                onChange={e => updateField('name', e.target.value)}
                maxLength={120}
                className="mt-2 bg-zinc-950 border-zinc-800"
                placeholder="Artist, curator or label name"
              />
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                Profile type
              </label>
              <Select
                value={form.profile_type}
                onValueChange={value => updateField('profile_type', value)}
              >
                <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROFILE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                Email
              </label>
              <Input
                value={user?.email || ''}
                readOnly
                disabled
                className="mt-2 bg-zinc-950 border-zinc-800"
              />
            </div>

            <div className="flex items-center gap-3 pb-2">
              <div>
                <div className="text-sm text-zinc-200">
                  Show email on public profile
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {form.email_public ? 'Public' : 'Private'}
                </div>
              </div>
              <Switch
                checked={form.email_public}
                onCheckedChange={value => updateField('email_public', value)}
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">
              Bio
            </label>
            <Textarea
              value={form.bio}
              onChange={e => updateField('bio', e.target.value)}
              maxLength={2000}
              rows={6}
              className="mt-2 bg-zinc-950 border-zinc-800"
              placeholder="Tell people who you are, what you do and what kind of music or opportunities you are looking for."
            />
            <div className="text-right text-[11px] text-zinc-600 mt-1 font-mono">
              {form.bio.length}/2000
            </div>
          </div>
        </section>

        <section className="border border-zinc-800 rounded-2xl bg-zinc-900/40 p-6">
          <div className="font-display text-xl mb-2">Social & music links</div>
          <p className="text-sm text-zinc-500 mb-5">
            Add public links that should appear on your profile.
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            {SOCIAL_FIELDS.map(([key, label]) => (
              <div key={key}>
                <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                  {label}
                </label>
                <Input
                  value={form.social_links?.[key] || ''}
                  onChange={e => updateSocial(key, e.target.value)}
                  maxLength={500}
                  className="mt-2 bg-zinc-950 border-zinc-800"
                  placeholder={`https://…`}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="border border-zinc-800 rounded-2xl bg-zinc-900/40 p-6">
          <div className="font-display text-xl mb-2">Public profile</div>
          <p className="text-sm text-zinc-500 mb-4">
            Share this page when you want someone to see your public TuneAmplifier profile.
          </p>

          <div className="flex flex-col md:flex-row gap-3">
            <Input
              readOnly
              value={publicUrl}
              className="bg-zinc-950 border-zinc-800"
            />
            <Button
              type="button"
              variant="outline"
              onClick={copyPublicLink}
              className="shrink-0"
            >
              Copy link
            </Button>
            <Link
              to={`/profile/${user?.user_id || ''}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-md border border-zinc-800 px-4 py-2 text-sm text-zinc-300 hover:text-white hover:border-zinc-600 transition-colors"
            >
              View profile
            </Link>
          </div>
        </section>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={saveProfile}
            disabled={saving}
            className="btn-neon rounded-full font-display px-6"
          >
            {saving ? 'Saving…' : 'Save profile'}
          </Button>
        </div>
      </div>
    </div>
  );
}
